import { defineAnalyticsEvent } from '@heidi/runtime/shell';

import type {
  TemplateCreationRecordCreatedProps,
  TemplateGenerationProps,
  TemplateOpenedProps,
} from '../contracts/template-authoring-analytics.types';
import type { TemplateEditorUpdatedProps } from '../contracts/template-editor-analytics.types';
import type { TemplateQualityGateEventProps } from '../contracts/template-quality-gate-analytics.types';

/**
 * The five analytics events the templates domain owns outright, and the single
 * declaration site for each of their names (D9 ruling 27).
 *
 * FROZEN. Each `name` is the key a warehouse schema and its dashboards are
 * built on, so a rename is a new event and a second spelling silently splits a
 * metric. `template_quality_gate` carries the extra weight of being a
 * regulatory control's audit trail: the gate-went-dark and fail-open monitors
 * query it by name.
 *
 * `builds` is omitted throughout, i.e. `cloud-only` (D6). The on-prem appliance
 * ships without the SaaS analytics SDKs, so an event that reached the on-prem
 * registry would be a declaration nothing can emit.
 *
 * WHY NOT MORE. The shared-template preview reports three further things — a
 * `screen_viewed`, its two CTA `button_clicked`s and the share CTA's — and
 * those are the app's CROSS-DOMAIN generic
 * events. No domain owns `button_clicked`, so no domain may declare it: a name
 * is globally unique and the second declarer would be a collision
 * (`duplicate-event-name` in `buildAppRegistry`, and the event-reference gate's
 * third arm). What templates owns of those three is their `screen_name` /
 * `action` vocabulary, which is in `../contracts/*-analytics.types.ts` with the
 * payloads.
 *
 * WHAT IS NOT HERE: the emit, and the routing. A package declares its events
 * and never imports a tracker (ruling 24) — the `track()` calls and the
 * `{ amplitude: false }` routing stay app-side in
 * `src/models/analytics/features/`, keyed by the literal names these
 * descriptors carry.
 */
export const templateEditorUpdated = defineAnalyticsEvent<TemplateEditorUpdatedProps>()({
  name: 'template_editor_updated',
  description: 'Template editor content was edited in the example or structure tab',
});

export const templateQualityGate = defineAnalyticsEvent<TemplateQualityGateEventProps>()({
  name: 'template_quality_gate',
  description: 'The UK/EU template quality attestation was shown, decided, or bypassed',
});

// Template authoring. All three emit through a `lib/analytics/scribe/` host
// called from `features/templateBuilder/**`, plus `useTemplateEditHandler` in
// the app shell for `template_opened`.
export const templateGeneration = defineAnalyticsEvent<TemplateGenerationProps>()({
  name: 'template_generation',
  description:
    'Template content generation was triggered from chat, example or structure',
});

export const templateCreationRecordCreated =
  defineAnalyticsEvent<TemplateCreationRecordCreatedProps>()({
    name: 'template_creation_record_created',
    description: 'A template creation record was started, carrying its request methods',
  });

export const templateOpened = defineAnalyticsEvent<TemplateOpenedProps>()({
  name: 'template_opened',
  description: 'An existing template was opened for editing',
});
